/**
 * Phone button for the officer call reminders. The two states are weighted so the eye goes
 * to the calls still owed, and are told apart by ring and motion rather than by dimming
 * (cell colours are already dimmed/darkened for other features):
 *   active  a call is still owed — a solid white chip with a thin theme-blue ring and a
 *           phone, wobbling now and then. Reads as a button on every cell colour.
 *   done    the officer was told — a pale theme-tinted chip with a soft slate tick instead of
 *           the phone, no motion. Reads as "finished" but stays quieter than the active chip,
 *           and is clickable to re-arm.
 * Used on each block of the counter grid and in an officer's label in the officer tab.
 *
 * `hitWidth` / `hitHeight` make the tappable area bigger than the visible chip (the officer
 * tab sits it next to the hide and delete buttons, where a fingertip must not hit those).
 */

import { useNotification } from '@/contexts/NotificationContext';
import { NOTIFICATION_SIZE } from '@/utils/notificationLayout';
import { sc } from '@/utils/uiScale';

/** The theme blue of the web app (see index.css). */
const INK = '#4338ca';
/** Done state: a pale theme-tinted chip with a soft slate tick, so it recedes next to the active chip. */
const DONE_FILL = '#eef2ff';
const DONE_RING = '#c7d2fe';
const DONE = '#94a3b8';

const PHONE_PATH =
  'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z';

/** The round chip itself — also drawn in the toast that follows a click, so the two match. */
export function CallChip({ active, size }: { active: boolean; size: number }) {
  const glyph = Math.max(6, Math.round(size * 0.58));
  const tick = Math.max(7, Math.round(size * 0.56));
  return (
    <span
      className={active ? 'call-phone-active' : undefined}
      style={{
        position: 'relative', boxSizing: 'border-box', width: size, height: size, borderRadius: '50%',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: active ? '#fff' : DONE_FILL,
        border: `1px solid ${active ? INK : DONE_RING}`,
        boxShadow: active ? '0 1px 2px rgba(0,0,0,0.3)' : undefined,
        animation: active ? 'call-phone-wobble 3.2s ease-in-out infinite' : undefined,
        transformOrigin: '50% 50%',
      }}
    >
      {active ? (
        <svg
          width={glyph} height={glyph} viewBox="0 0 24 24" aria-hidden="true"
          fill="none" stroke={INK} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"
        >
          <path d={PHONE_PATH} />
        </svg>
      ) : (
        <svg width={tick} height={tick} viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 12.5l5.5 5.5L20 6.5" fill="none" stroke={DONE} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

export function CallPhoneButton({
  active,
  onClick,
  title,
  size = sc(14),
  hitWidth,
  hitHeight,
  disabled = false,
  style,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  /** Diameter of the visible round chip, px. */
  size?: number;
  /** Width / height of the tappable area, px — default: the chip itself. */
  hitWidth?: number;
  hitHeight?: number;
  disabled?: boolean;
  style?: React.CSSProperties;
}) {
  const { notify } = useNotification();
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={!active}
      disabled={disabled}
      data-testid="call-phone"
      data-active={active ? 'true' : 'false'}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
        // `active` = a call was owed, so this click marks the officer informed (and the reverse).
        if (active) notify('Officer informed on roster changes', 'success', { icon: <CallChip active={false} size={NOTIFICATION_SIZE.chip} />, key: 'call-toggle' });
        else notify('Marked as not informed yet', 'info', { icon: <CallChip active size={NOTIFICATION_SIZE.chip} />, key: 'call-toggle' });
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      style={{
        width: hitWidth ?? size, height: hitHeight ?? size, padding: 0, margin: 0, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'none', border: 'none', lineHeight: 0,
        cursor: disabled ? 'default' : 'pointer',
        WebkitTapHighlightColor: 'rgba(102,126,234,0.25)',
        ...style,
      }}
    >
      <CallChip active={active} size={size} />
    </button>
  );
}
